import { useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  type SelectChangeEvent
} from '@mui/material';
import type { Company, Group, Role, User } from '@esg/esg-global-types';

import CompanyAccessConfig from './shared/CompanyAccessConfig';
import { modules } from '../../lib/app/navigation';
import { UserContext } from '../../context/UserContext';
import { checkUserGroupAdmin } from '../../util/user_access';

interface EditUserPermissionsTabProps {
  draft: User;
  groups: Group[];
  roles: Role[];
  onChange: (patch: Partial<User>) => void;
}

type PresetRole = 'viewer' | 'editor' | 'custom';

const ROLE_PRESETS: Array<{ value: PresetRole; label: string; description: string }> = [
  {
    value: 'viewer',
    label: 'Viewer',
    description: 'Read access to every view except Configuration.'
  },
  {
    value: 'editor',
    label: 'Editor',
    description: 'Write access to every view except Configuration.'
  },
  {
    value: 'custom',
    label: 'Custom',
    description: 'Per-view permission grid (advanced).'
  }
];

/** Permissions tab: Viewer/Editor are blanket roles; Custom opens a modal with the per-view grid. */
const EditUserPermissionsTab = ({
  draft,
  groups,
  roles,
  onChange
}: EditUserPermissionsTabProps) => {
  const { user: currentUser } = useContext(UserContext);
  const isSuperAdmin = !!currentUser?.super_admin;

  // Scope per-company roles to the groups the signed-in viewer administers.
  // Reads are not scoped server-side, so a non-super group admin must not see
  // — or bulk-apply roles to — companies in groups they don't administer.
  // Mirrors the Access tab / CompanyChipStack scoping.
  const canManageGroup = (groupId: string): boolean =>
    isSuperAdmin || (!!currentUser && checkUserGroupAdmin(currentUser, groupId));

  const companyMembershipRows = useMemo(
    () =>
      Object.entries(draft.groups ?? {})
        .filter(([group_id]) => canManageGroup(group_id))
        .flatMap(([group_id, group_data]) =>
          Object.entries(group_data.companies ?? {}).map(([company_id, company_data]) => ({
            group_id,
            company_id,
            role: company_data.role,
            group_name: groups.find((g) => g.id === group_id)?.name ?? group_id,
            company_name:
              groups.find((g) => g.id === group_id)?.companies?.find((c) => c.id === company_id)
                ?.name ?? company_id
          }))
        ),
    [draft.groups, groups, currentUser, isSuperAdmin]
  );

  const [bulkPreset, setBulkPreset] = useState<PresetRole>('viewer');
  const [configTarget, setConfigTarget] = useState<{
    group_id: string;
    company_id: string;
  } | null>(null);

  const changeRoleForCompany = (group_id: string, company_id: string, role: string) => {
    const groupsPatch = { ...(draft.groups ?? {}) };
    const group = { ...groupsPatch[group_id] };
    const companies = { ...(group.companies ?? {}) };
    companies[company_id] = { ...companies[company_id], role };
    group.companies = companies;
    groupsPatch[group_id] = group;
    onChange({ groups: groupsPatch });
    // Setting Custom immediately opens the configuration modal so the per-view
    // grid is configured in place rather than on a separate scroll target.
    if (role === 'custom') {
      setConfigTarget({ group_id, company_id });
    } else if (configTarget?.group_id === group_id && configTarget?.company_id === company_id) {
      setConfigTarget(null);
    }
  };

  const configGroup: Group | undefined = configTarget
    ? groups.find((g) => g.id === configTarget.group_id)
    : undefined;
  const configCompany: Company | undefined = configTarget
    ? configGroup?.companies?.find((c: Company) => c.id === configTarget.company_id)
    : undefined;

  const applyToAll = () => {
    const groupsPatch = { ...(draft.groups ?? {}) };
    for (const [gid, g] of Object.entries(groupsPatch)) {
      // Only bulk-apply within the viewer's administered groups; out-of-scope
      // memberships are left untouched (and the server would reject them).
      if (!canManageGroup(gid)) continue;
      const companies = { ...(g.companies ?? {}) };
      for (const cid of Object.keys(companies)) {
        companies[cid] = { ...companies[cid], role: bulkPreset };
      }
      groupsPatch[gid] = { ...g, companies };
    }
    onChange({ groups: groupsPatch });
  };

  if (draft.super_admin) {
    return (
      <Stack spacing={2} sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Super admins have full system access, so per-company roles and permissions are not
          configured here.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Apply role preset to all companies
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={bulkPreset}
          onChange={(_, val: PresetRole | null) => val && setBulkPreset(val)}
        >
          {ROLE_PRESETS.map((preset) => (
            <ToggleButton key={preset.value} value={preset.value}>
              {preset.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box
          component="button"
          type="button"
          onClick={applyToAll}
          sx={{
            background: 'transparent',
            border: 1,
            borderColor: 'primary.main',
            color: 'primary.main',
            px: 2,
            py: 0.5,
            borderRadius: 1,
            cursor: 'pointer',
            fontFamily: 'inherit'
          }}
        >
          Apply to all companies
        </Box>
        <Typography variant="caption" color="text.disabled">
          {ROLE_PRESETS.find((p) => p.value === bulkPreset)?.description}
        </Typography>
      </Box>

      <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 2 }}>
        Per-company role
      </Typography>
      <TableContainer component={Box} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Group</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Role</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {companyMembershipRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.disabled" sx={{ py: 2 }}>
                    No company memberships to configure.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              companyMembershipRows.map((row) => (
                <TableRow key={`${row.group_id}-${row.company_id}`}>
                  <TableCell>{row.group_name}</TableCell>
                  <TableCell>{row.company_name}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={row.role}
                      onChange={(e: SelectChangeEvent<string>) =>
                        changeRoleForCompany(row.group_id, row.company_id, e.target.value)
                      }
                      sx={{ minWidth: 120 }}
                    >
                      {!ROLE_PRESETS.some((preset) => preset.value === row.role) && (
                        <MenuItem key={row.role} value={row.role}>
                          {row.role}
                        </MenuItem>
                      )}
                      {ROLE_PRESETS.map((preset) => (
                        <MenuItem key={preset.value} value={preset.value}>
                          {preset.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    {row.role === 'custom' && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setConfigTarget({
                            group_id: row.group_id,
                            company_id: row.company_id
                          })
                        }
                      >
                        Configure
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!configTarget && !!configGroup && !!configCompany}
        onClose={() => setConfigTarget(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Configure custom access
          {configCompany ? ` — ${configCompany.name}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {configGroup && configCompany && (
            <CompanyAccessConfig
              key={`${configGroup.id}-${configCompany.id}`}
              user_row={draft}
              chosen_group={configGroup}
              chosen_company={configCompany}
              roles={roles}
              modules={modules}
              handleUserChange={(new_user: User) => onChange({ groups: new_user.groups })}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setConfigTarget(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default EditUserPermissionsTab;
